import type { MeetingListResult } from '@fos/shared/contracts/meetings';
import { CalendarDays } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { MeetingRow } from './MeetingRow';
import { NewMeetingDialog } from './NewMeetingDialog';

/**
 * Spotkania klienta albo projektu do wstawienia w profil (zakładka „Spotkania”): nadchodzące (od najbliższego)
 * i ostatnie (od najnowszego), „Nowe spotkanie” z uzupełnionym klientem / projektem, link do pełnej listy.
 *
 *   <MeetingRows clientId={client.id} />
 *   <MeetingRows projectId={project.id} />
 */
export function MeetingRows({
  clientId,
  projectId,
  upcomingLimit = 5,
  pastLimit = 10,
  allowAdd = true,
}: {
  clientId?: string;
  projectId?: string;
  upcomingLimit?: number;
  pastLimit?: number;
  /** „Nowe spotkanie” (dla osób z edycją Klientów) */
  allowAdd?: boolean;
}) {
  const access = useAccess();
  const [adding, setAdding] = useState(false);
  const enabled = access.can('clients');
  const filter = { ...(clientId ? { clientId } : {}), ...(projectId ? { projectId } : {}) };
  const upcoming = trpc.meetings.list.useQuery({ ...filter, when: 'upcoming', limit: upcomingLimit }, { enabled });
  const past = trpc.meetings.list.useQuery({ ...filter, when: 'past', limit: pastLimit }, { enabled });

  if (!enabled) return null;
  const canAdd = allowAdd && access.can('clients', 'edit');
  const addButton = canAdd ? (
    <Button variant="ghost" size="s" onClick={() => setAdding(true)}>
      Nowe spotkanie
    </Button>
  ) : undefined;
  const allLink = new URLSearchParams();
  if (clientId) allLink.set('klient', clientId);
  if (projectId) allLink.set('projekt', projectId);

  const pending = upcoming.isPending || past.isPending;
  const failed = upcoming.isError || past.isError;
  const empty = !pending && !failed && upcoming.data?.items.length === 0 && past.data?.items.length === 0;

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {pending ? (
        <Section>
          <ListSkeleton rows={3} />
        </Section>
      ) : failed ? (
        <Section>
          <ErrorState
            compact
            onRetry={() => {
              void upcoming.refetch();
              void past.refetch();
            }}
            retrying={upcoming.isFetching || past.isFetching}
          />
        </Section>
      ) : empty ? (
        <Section>
          <EmptyState
            icon={CalendarDays}
            title="Nie ma jeszcze spotkań"
            description="Dodaj spotkanie z notatkami albo transkrypcją, a AI przygotuje podsumowanie i zadania."
            action={addButton}
          />
        </Section>
      ) : (
        <>
          <Group title="Nadchodzące" result={upcoming.data} hideClient={!!clientId} action={addButton} />
          <Group title="Ostatnie" result={past.data} hideClient={!!clientId} />
          {(upcoming.data?.truncated || past.data?.truncated) && (
            <Link
              to={`/spotkania?${allLink.toString()}`}
              className="self-start text-body-s font-medium text-accent hover:underline"
            >
              Wszystkie spotkania
            </Link>
          )}
        </>
      )}
      {canAdd && (
        <NewMeetingDialog
          open={adding}
          onOpenChange={setAdding}
          defaultClientId={clientId}
          defaultProjectId={projectId}
        />
      )}
    </div>
  );
}

function Group({
  title,
  result,
  hideClient,
  action,
}: {
  title: string;
  result: MeetingListResult | undefined;
  hideClient: boolean;
  action?: ReactNode;
}) {
  if (!result || (result.items.length === 0 && !action)) return null;
  return (
    <Section aria-label={title}>
      <SectionHeader title={title} action={action} />
      {result.items.length === 0 ? (
        <EmptyState compact title="Brak zaplanowanych spotkań." />
      ) : (
        <ul>
          {result.items.map((m) => (
            <li key={m.id}>
              <MeetingRow meeting={m} hideClient={hideClient} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
